/**
 * Onay banner'ı ve detay penceresi.
 *
 * Kendi DOM'unu kendi yaratır ve kendi toplar — index.html'de bu modüle ait
 * tek bir etiket yok. Eski KVKK kodu markup'ı index.html'e, mantığı auth.js'e,
 * stili style bloğuna dağıtmıştı; kaldırmak dört yerde ameliyat gerektiriyordu.
 * Burada kaldırmak klasörü silmek demek.
 */

import { DOCUMENTS, type LegalDocument, type RegionCode } from './documents.ts';
import { needsConsent, saveConsent, type ConsentChoice } from './store.ts';

const ROOT_ID = 'consent-root';

let root: HTMLElement | null = null;
let onDismiss: (() => void) | null = null;

const el = (tag: string, style: string, text?: string): HTMLElement => {
  const node = document.createElement(tag);
  node.setAttribute('style', style);
  if (text !== undefined) node.textContent = text;
  return node;
};

function detailModal(doc: LegalDocument): HTMLElement {
  const overlay = el('div',
    'position:fixed;inset:0;background:#000c;z-index:100001;display:flex;'
    + 'align-items:center;justify-content:center;padding:20px');

  const panel = el('div',
    'background:var(--card,#1a1a1a);border-radius:16px;padding:24px;max-width:480px;'
    + 'width:100%;max-height:80vh;overflow-y:auto;border:1px solid var(--div,#2e2e2e)');

  const head = el('div',
    'font-size:15px;font-weight:700;margin-bottom:14px;display:flex;'
    + 'justify-content:space-between;align-items:center;gap:12px');
  head.appendChild(el('span', 'color:var(--t1,#F5F5F5)', doc.title));

  const close = el('button',
    'background:none;border:none;color:var(--t2,#888);font-size:20px;cursor:pointer;'
    + 'line-height:1;padding:0 4px', '×');
  close.setAttribute('aria-label', doc.labels.close);
  close.addEventListener('click', () => overlay.remove());
  head.appendChild(close);
  panel.appendChild(head);

  const body = el('div', 'font-size:12px;color:var(--t2,#888);line-height:1.8');
  for (const section of doc.body) {
    const b = el('b', 'color:var(--t1,#F5F5F5)', section.heading + ': ');
    const p = el('div', 'margin-bottom:12px');
    p.appendChild(b);
    p.appendChild(document.createTextNode(section.text));
    body.appendChild(p);
  }
  panel.appendChild(body);

  overlay.appendChild(panel);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  return overlay;
}

function build(region: RegionCode, doc: LegalDocument): HTMLElement {
  const wrap = el('div', 'position:fixed;bottom:0;left:0;right:0;z-index:99999;'
    + 'background:var(--card,#1a1a1a);border-top:1px solid var(--div,#2e2e2e);'
    + 'padding:14px 18px calc(14px + env(safe-area-inset-bottom,0px));'
    + 'box-shadow:0 -4px 24px #0009');
  wrap.id = ROOT_ID;
  wrap.setAttribute('role', 'dialog');
  wrap.setAttribute('aria-label', doc.title);

  const inner = el('div', 'max-width:680px;margin:0 auto;display:flex;flex-direction:column;gap:10px');
  inner.appendChild(el('div', 'font-size:13px;font-weight:700;color:var(--t1,#F5F5F5)', doc.title));

  const summary = el('div', 'font-size:12px;color:var(--t2,#888);line-height:1.7');
  summary.appendChild(document.createTextNode(doc.summary + ' '));
  const detail = el('a', 'color:var(--red,#FF1A1A);text-decoration:underline;cursor:pointer', doc.labels.detail);
  detail.setAttribute('href', '#');
  detail.addEventListener('click', (e) => {
    e.preventDefault();
    document.body.appendChild(detailModal(doc));
  });
  summary.appendChild(detail);
  inner.appendChild(summary);

  const buttons = el('div', 'display:flex;gap:8px;flex-wrap:wrap');
  const decide = (choice: ConsentChoice) => {
    saveConsent({ choice, region, documentVersion: doc.version, at: new Date().toISOString() });
    teardown();
    onDismiss?.();
  };

  const accept = el('button',
    'padding:9px 20px;border-radius:9px;border:none;background:var(--red,#FF1A1A);'
    + 'color:#fff;font-size:13px;font-weight:600;cursor:pointer', doc.labels.accept);
  accept.addEventListener('click', () => decide('accepted'));

  const reject = el('button',
    'padding:9px 20px;border-radius:9px;border:1px solid var(--div,#444);'
    + 'background:transparent;color:var(--t2,#888);font-size:13px;cursor:pointer', doc.labels.reject);
  reject.addEventListener('click', () => decide('necessary_only'));

  buttons.appendChild(accept);
  buttons.appendChild(reject);
  inner.appendChild(buttons);
  wrap.appendChild(inner);
  return wrap;
}

/** Gerekiyorsa banner'ı göster. Gerekmiyorsa hiçbir şey yapmaz. */
export function show(region: RegionCode, opts: { onDismiss?: () => void } = {}): void {
  const doc = DOCUMENTS[region];
  if (!doc || !needsConsent(region, doc.version)) return;
  if (root) return;

  onDismiss = opts.onDismiss ?? null;
  root = build(region, doc);
  document.body.appendChild(root);
}

/** Modülün açtığı her şeyi kapat. */
export function teardown(): void {
  root?.remove();
  root = null;
  document.getElementById(ROOT_ID)?.remove();
  onDismiss = null;
}
